/* eslint-disable indent */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button, DatePicker, message, Input } from "antd";
const { RangePicker } = DatePicker;
import { ArrowLeftOutlined, SearchOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { formatIndianNumber, formatIndianCurrency, mapReportCategoryToFigureType, mapBusinessTypeToFigureSubtype, mapZoneNameToZoneId } from "../../../../utils";
import { DirectorDashboardService3 } from "../../../../services/api/directorDashboardService3";
import "./style.scss";
import "../style.scss";



const HubWiseView3 = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedDate, setSelectedDate] = useState(moment());
    const [selectedDateRange, setSelectedDateRange] = useState([moment().startOf("month"), moment()]);

    const [hubWiseData, setHubWiseData] = useState([]);
    const [zone, setZone] = useState("");
    const [businessType, setBusinessType] = useState("");
    const [reportType, setReportType] = useState("Piece Rate");
    const [reportCategory, setReportCategory] = useState("Business Data");
    const [dataType, setDataType] = useState("today"); // "today" or "range"
    const hasLoadedRef = useRef(false);
    const isInitializingRef = useRef(true);

    useEffect(() => {
        // Get zone, report category, type, and data type from location state if available
        const zoneValue = location.state?.zone || "";
        const category = location.state?.reportCategory || "Business Data";
        const type = location.state?.reportType || "Piece Rate";
        const typeMode = location.state?.type || "today";
        const businessTypeValue = location.state?.businessType || "";

        isInitializingRef.current = true;

        setZone(zoneValue);
        setReportCategory(category);
        setReportType(type);
        setDataType(typeMode);
        setBusinessType(businessTypeValue);

        if (typeMode === "today") {
            if (location.state?.date) {
                setSelectedDate(moment(location.state.date, "YYYY-MM-DD", true));
            }
        } else {
            // For range type
            if (location.state?.date && location.state?.endDate) {
                const startDate = moment(location.state.date, "YYYY-MM-DD", true);
                const endDate = moment(location.state.endDate, "YYYY-MM-DD", true);
                if (startDate.isValid() && endDate.isValid()) {
                    setSelectedDateRange([startDate, endDate]);
                }
            } else if (location.state?.date) {
                const dateValue = moment(location.state.date, "YYYY-MM-DD", true);
                if (dateValue.isValid()) {
                    setSelectedDateRange([dateValue, dateValue]);
                }
            }
        }

        // Allow state updates to complete
        setTimeout(() => {
            isInitializingRef.current = false;
        }, 0);
    }, [location.state]);

    const loadHubWiseData = useCallback(async () => {
        if (!zone || isInitializingRef.current) return;

        // Use default businessType if empty
        const finalBusinessType = businessType || (reportCategory === "Delivery Data" ? "Total DRS" : "Account");



        try {
            const figureType = mapReportCategoryToFigureType(reportCategory);
            const figureSubtype = mapBusinessTypeToFigureSubtype(finalBusinessType, reportCategory);
            const zoneId = mapZoneNameToZoneId(zone);

            if (!zoneId) {
                message.error("Invalid zone name");
                setHubWiseData([]);

                return;
            }

            let fromDate, toDate;
            if (dataType === "today") {
                fromDate = selectedDate.format("YYYY-MM-DD");
                toDate = selectedDate.format("YYYY-MM-DD");
            } else {
                if (selectedDateRange && selectedDateRange.length === 2 && selectedDateRange[0] && selectedDateRange[1]) {
                    fromDate = selectedDateRange[0].format("YYYY-MM-DD");
                    toDate = selectedDateRange[1].format("YYYY-MM-DD");
                } else {
                    fromDate = location.state?.date || moment().startOf("month").format("YYYY-MM-DD");
                    toDate = location.state?.endDate || location.state?.date || moment().format("YYYY-MM-DD");
                }
            }

            const response = await DirectorDashboardService3.getZoneHubCpData(
                figureType,
                figureSubtype,
                fromDate,
                toDate,
                zoneId
            );

            if (response?.data?.hubWiseSummary) {
                // Format value based on report category
                const formatValue = (value) => {
                    const numValue = typeof value === "number" ? value : parseFloat(value) || 0;
                    if (reportCategory === "Business Data") {
                        return formatIndianCurrency(numValue);
                    } else {
                        return formatIndianNumber(numValue);
                    }
                };

                // Transform hubWiseSummary array to table format
                const transformedData = response.data.hubWiseSummary
                    .filter((item) => {
                        // Keep Total row, filter out 0 values for others
                        if (item.hubName === "Total" || item.hubId === "Total") return true;
                        const val = typeof item.total === "number" ? item.total : parseFloat(item.total) || 0;
                        return val > 0;
                    })
                    .map((item) => ({
                        key: item.hubId || item.hubName,
                        hub: item.hubName,
                        hubId: item.hubId,
                        total: formatValue(item.total || 0),
                        rawTotal: item.total || 0, // Keep raw value for calculations
                    }));
                setHubWiseData(transformedData);
            } else {
                setHubWiseData([]);
                message.warning("No data available for the selected date range");
            }
        } catch (error) {
            console.error("Error loading hub wise data:", error);
            message.error(error?.message || "Failed to load hub wise data");
            setHubWiseData([]);
        }
    }, [reportCategory, businessType, zone, dataType, selectedDate, selectedDateRange, location.state]);

    useEffect(() => {
        // Load data when component mounts or reportCategory/zone/dataType changes
        // Wait for initialization to complete
        const checkAndLoad = () => {
            if (isInitializingRef.current) {
                setTimeout(checkAndLoad, 50);
                return;
            }

            // Ensure we have required data
            if (!zone || !reportCategory) {
                return;
            }

            // Use default businessType if empty
            const finalBusinessType = businessType || (reportCategory === "Delivery Data" ? "Total DRS" : "Account");

            const dateKey = dataType === "today"
                ? selectedDate.format("YYYY-MM-DD")
                : `${selectedDateRange[0]?.format("YYYY-MM-DD")}-${selectedDateRange[1]?.format("YYYY-MM-DD")}`;
            const loadKey = `${reportCategory}-${finalBusinessType}-${zone}-${dataType}-${dateKey}`;

            if (!hasLoadedRef.current || hasLoadedRef.current !== loadKey) {
                hasLoadedRef.current = loadKey;
                loadHubWiseData();
            }
        };

        checkAndLoad();
    }, [reportCategory, businessType, zone, dataType, selectedDate, selectedDateRange, loadHubWiseData]);

    const handleDateSubmit = () => {
        hasLoadedRef.current = null; // Reset to allow reload
        loadHubWiseData();
    };

    const getDisplayDate = () => {
        if (dataType === "today") {
            return selectedDate.format("DD/MM/YYYY");
        } else {
            if (selectedDateRange && selectedDateRange.length === 2 && selectedDateRange[0] && selectedDateRange[1]) {
                return `${selectedDateRange[0].format("DD/MM/YYYY")} - ${selectedDateRange[1].format("DD/MM/YYYY")}`;
            }
            return `${moment().startOf("month").format("DD/MM/YYYY")} - ${moment().format("DD/MM/YYYY")}`;
        }
    };
    const displayDate = getDisplayDate();

    const handleGoBack = () => {
        // Always navigate back to zone-list
        navigate("/zone-list2", {
            state: {
                date: selectedDate.format("YYYY-MM-DD"),
                reportType,
                reportCategory,
                type: dataType,
                businessType: location.state?.businessType || "",
            },
        });
    };

    const handleHubClick = (record) => {
        // Don't navigate if it's the Total row
        if (record.hub === "Total") {
            return;
        }
        // Navigate to CP-wise view with hub information
        const routeMap = {
            "Business Data": "/cp-wise-business-data2",
            "Shipment Counts": "/cp-wise-shipment-counts2",
            "Delivery Data": "/cp-wise-shipment-counts2",
        };

        let dateToPass, endDateToPass;
        if (dataType === "today") {
            dateToPass = selectedDate.format("YYYY-MM-DD");
            endDateToPass = null;
        } else {
            if (selectedDateRange && selectedDateRange.length === 2) {
                dateToPass = selectedDateRange[0].format("YYYY-MM-DD");
                endDateToPass = selectedDateRange[1].format("YYYY-MM-DD");
            } else {
                dateToPass = location.state?.date || moment().startOf("month").format("YYYY-MM-DD");
                endDateToPass = location.state?.endDate || moment().format("YYYY-MM-DD");
            }
        }

        navigate(routeMap[reportCategory] || "/cp-wise-business-data2", {
            state: {
                zone,
                hub: record.hub,
                hubId: record.hubId, // Pass hubId for API call
                reportType,
                reportCategory,
                businessType,
                type: dataType,
                date: dateToPass,
                endDate: endDateToPass,
            },
        });
    };



    return (
        <div className="director-dashboard hub-wise-page">
            {/* Unified Header Bar */}
            <div className="hub-wise-header">
                <div className="header-left">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleGoBack}
                        className="back-button"
                    />
                    <span className="page-title">
                        Hub Wise {reportCategory} - {displayDate} - {zone} {reportCategory === "Business Data" ? `(${reportType})` : ""}
                    </span>
                </div>
                <div className="header-right">
                    <Input
                        placeholder="Search by Hub Name"
                        prefix={<SearchOutlined />}
                        className="search-input"
                    />
                    {dataType === "range" ? (
                        <RangePicker
                            format="DD/MM/YYYY"
                            value={selectedDateRange}
                            onChange={(dates) => {
                                if (dates && dates.length === 2) {
                                    setSelectedDateRange([dates[0], dates[1]]);
                                } else {
                                    setSelectedDateRange([moment().startOf("month"), moment()]);
                                }
                            }}
                            className="hub-wise-date-picker"
                        />
                    ) : (
                        <DatePicker
                            format="DD/MM/YYYY"
                            value={selectedDate}
                            onChange={(date) => setSelectedDate(date || moment())}
                            className="hub-wise-date-picker"
                        />
                    )}
                    <Button type="primary" onClick={handleDateSubmit} className="hub-wise-submit-btn">
                        Submit
                    </Button>
                </div>
            </div>

            {/* Hub List Grid */}
            <div className="hub-wise-grid">
                {/* Hub Cards */}
                {hubWiseData.map((item) => {
                    // Skip Total row in grid, it's shown in footer
                    if (item.hub === "Total") return null;

                    return (
                        <div
                            className="hub-wise-card"
                            key={item.key}
                            onClick={() => handleHubClick(item)}
                        >
                            <div className="card-header">
                                <span className="hub-name">{item.hub}</span>
                                <InfoCircleOutlined className="info-icon" />
                            </div>
                            <div className="card-value">
                                {item.total}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Total Footer */}
            {hubWiseData.length > 0 && (
                <div className="hub-wise-footer">
                    <div className="footer-left">
                        <span className="footer-label">Total Hub Count : </span>
                        {/* Subtract 1 for the 'Total' row if it exists in data */}
                        <span className="footer-value">
                            {Math.max(0, hubWiseData.filter(i => i.hub !== "Total").length).toString().padStart(2, "0")}
                        </span>
                    </div>
                    <div className="footer-right">
                        <span className="footer-label">Total Amount : </span>
                        <span className="footer-value">
                            {hubWiseData.find(i => i.hub === "Total")?.total || "0"}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HubWiseView3;

